import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/** In-memory span representation */
interface Span {
  spanId: string;
  traceId: string;
  name: string;
  startTime: number;
  endTime?: number;
  status?: 'ok' | 'error';
  attributes: IntegrationParams;
  events: Array<{ name: string; timestamp: number; attributes?: IntegrationParams }>;
}

/** Shape of a stored metric */
interface Metric {
  name: string;
  type: 'counter' | 'gauge' | 'histogram';
  value: number;
  values: number[];
  labels: IntegrationParams;
}

/** Generate a random hex id of the given byte-length. */
function randomHexId(bytes: number): string {
  const arr: string[] = [];
  for (let i = 0; i < bytes; i++) {
    arr.push(Math.floor(Math.random() * 256).toString(16).padStart(2, '0'));
  }
  return arr.join('');
}

/**
 * OpenTelemetry integration with in-memory backend.
 *
 * Tracks spans and metrics internally for development and testing purposes.
 * No actual OpenTelemetry SDK dependency is required.
 */
export class OtelIntegration extends BaseIntegration {
  private spans: Map<string, Span> = new Map();
  private metrics: Map<string, Metric> = new Map();

  constructor(config: IntegrationConfig) {
    super(config);
    this.logger.info('OTel integration initialized (in-memory backend)');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'startSpan':
          data = await this.executeWithRetry(() => this.startSpan(params));
          break;
        case 'endSpan':
          data = await this.executeWithRetry(() => this.endSpan(params));
          break;
        case 'addEvent':
          data = await this.executeWithRetry(() => this.addEvent(params));
          break;
        case 'recordMetric':
          data = await this.executeWithRetry(() => this.recordMetric(params));
          break;
        case 'getSpan':
          data = await this.executeWithRetry(() => this.getSpan(params));
          break;
        case 'getMetrics':
          data = await this.executeWithRetry(() => this.getAllMetrics());
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  private async startSpan(
    params: IntegrationParams,
  ): Promise<{ spanId: string; traceId: string }> {
    const name = params.name as string;
    const attributes = (params.attributes as IntegrationParams | undefined) ?? {};
    const spanId = randomHexId(8);
    const traceId = (params.traceId as string | undefined) ?? randomHexId(16);

    const span: Span = {
      spanId,
      traceId,
      name,
      startTime: Date.now(),
      attributes,
      events: [],
    };

    this.spans.set(spanId, span);
    this.logger.debug('Span started', { spanId, traceId, name });

    return { spanId, traceId };
  }

  private async endSpan(
    params: IntegrationParams,
  ): Promise<{ ended: boolean }> {
    const spanId = params.spanId as string;
    const status = params.status as 'ok' | 'error' | undefined;

    const span = this.spans.get(spanId);
    if (!span) {
      this.logger.warn('endSpan called for unknown span', { spanId });
      return { ended: false };
    }

    span.endTime = Date.now();
    if (status) {
      span.status = status;
    }

    this.logger.debug('Span ended', { spanId, status, duration: span.endTime - span.startTime });
    return { ended: true };
  }

  private async addEvent(
    params: IntegrationParams,
  ): Promise<{ added: boolean }> {
    const spanId = params.spanId as string;
    const name = params.name as string;
    const attributes = params.attributes as IntegrationParams | undefined;

    const span = this.spans.get(spanId);
    if (!span) {
      this.logger.warn('addEvent called for unknown span', { spanId });
      return { added: false };
    }

    span.events.push({
      name,
      timestamp: Date.now(),
      attributes,
    });

    this.logger.debug('Event added to span', { spanId, eventName: name });
    return { added: true };
  }

  private async recordMetric(
    params: IntegrationParams,
  ): Promise<{ recorded: boolean }> {
    const name = params.name as string;
    const value = params.value as number;
    const type = (params.type as 'counter' | 'gauge' | 'histogram' | undefined) ?? 'counter';
    const labels = (params.labels as IntegrationParams | undefined) ?? {};

    const existing = this.metrics.get(name);

    if (existing) {
      switch (type) {
        case 'counter':
          existing.value += value;
          break;
        case 'gauge':
          existing.value = value;
          break;
        case 'histogram':
          existing.values.push(value);
          break;
      }
      existing.labels = labels;
    } else {
      this.metrics.set(name, {
        name,
        type,
        value: type === 'histogram' ? 0 : value,
        values: type === 'histogram' ? [value] : [],
        labels,
      });
    }

    this.logger.debug('Metric recorded', { name, value, type });
    return { recorded: true };
  }

  private async getSpan(
    params: IntegrationParams,
  ): Promise<Span | null> {
    const spanId = params.spanId as string;
    const span = this.spans.get(spanId);
    return span ?? null;
  }

  private async getAllMetrics(): Promise<Record<string, Metric>> {
    const result: Record<string, Metric> = {};
    for (const [key, metric] of this.metrics) {
      result[key] = metric;
    }
    return result;
  }
}

// Auto-register
registerIntegration('otel', OtelIntegration);
